<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { Close } from '@element-plus/icons-vue';

import { useAgentPanel } from './useAgentPanel';
import { useAgentEmbedBridge } from './useAgentEmbedBridge';
import { AGENT_EMBED_URL } from './agent-addon.config';

const { panelOpen, panelWidth, isResizing, closePanel, beginResize, clampPanelWidthToViewport } =
  useAgentPanel();

const frameRef = ref<HTMLIFrameElement | null>(null);
useAgentEmbedBridge(frameRef);

const panelStyle = computed(() => ({
  width: `${panelWidth.value}px`,
}));

onMounted(() => {
  window.addEventListener('resize', clampPanelWidthToViewport);
});

onUnmounted(() => {
  window.removeEventListener('resize', clampPanelWidthToViewport);
});
</script>

<template>
  <Transition name="ax-agent-panel-slide">
    <aside
      v-if="panelOpen"
      class="ax-agent-panel"
      :class="{ 'ax-agent-panel--resizing': isResizing }"
      :style="panelStyle"
      aria-label="AI Agent"
    >
      <div
        class="ax-agent-panel__resize"
        role="separator"
        aria-orientation="vertical"
        aria-label="Agent 패널 너비 조절"
        @pointerdown="beginResize"
      >
        <span class="ax-agent-panel__resize-grip" aria-hidden="true" />
      </div>

      <button
        type="button"
        class="ax-agent-panel__close"
        aria-label="Agent 패널 닫기"
        @click="closePanel"
      >
        <el-icon size="16"><Close /></el-icon>
      </button>

      <iframe
        ref="frameRef"
        class="ax-agent-panel__frame"
        :class="{ 'ax-agent-panel__frame--blocked': isResizing }"
        :src="AGENT_EMBED_URL"
        title="AX-Agent"
        referrerpolicy="no-referrer-when-downgrade"
      />
    </aside>
  </Transition>
</template>

<style scoped>
.ax-agent-panel {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 20;
  min-width: 480px;
  pointer-events: auto;
  border-left: 1px solid var(--el-border-color);
  background: var(--el-bg-color);
  box-shadow: -4px 0 20px color-mix(in srgb, var(--el-text-color-primary) 10%, transparent);
  will-change: transform;
}

.ax-agent-panel--resizing {
  transition: none;
}

.ax-agent-panel__resize {
  position: absolute;
  top: 0;
  left: -5px;
  z-index: 3;
  width: 10px;
  height: 100%;
  cursor: col-resize;
  touch-action: none;
}

.ax-agent-panel__resize-grip {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 3px;
  height: 40px;
  border-radius: 999px;
  background: var(--el-border-color);
  transform: translate(-50%, -50%);
  transition: background 0.15s ease;
}

.ax-agent-panel__resize:hover .ax-agent-panel__resize-grip,
.ax-agent-panel--resizing .ax-agent-panel__resize-grip {
  background: var(--ax-color-primary);
}

.ax-agent-panel__close {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: 1px solid var(--el-border-color-lighter);
  border-radius: 6px;
  background: color-mix(in srgb, var(--el-bg-color) 92%, transparent);
  color: var(--el-text-color-secondary);
  cursor: pointer;
  backdrop-filter: blur(4px);
  transition:
    background 0.15s,
    border-color 0.15s,
    color 0.15s;
}

.ax-agent-panel__close:hover {
  border-color: var(--el-border-color);
  background: var(--el-bg-color);
  color: var(--el-text-color-primary);
}

.ax-agent-panel__frame {
  display: block;
  width: 100%;
  height: 100%;
  border: none;
  background: var(--el-bg-color);
}

.ax-agent-panel__frame--blocked {
  pointer-events: none;
}

:global(.ax-agent-panel-slide-enter-active),
:global(.ax-agent-panel-slide-leave-active) {
  transition: transform 0.28s cubic-bezier(0.4, 0, 0.2, 1);
}

:global(.ax-agent-panel-slide-enter-from),
:global(.ax-agent-panel-slide-leave-to) {
  transform: translateX(100%);
}
</style>
